import React, { forwardRef, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import { Button } from './Button';
import { TextInput } from './TextInput';
import './EmailAddressInput.css';
import { useConfig } from '../../config';
import axios from 'axios';
import { Res } from '../../state/api-resources';
import { ApiEmailInfo } from '../../types';
import { LoginForm } from './SuggestLoginForm';

export interface EmailAddressModel {
	email: string;
}

type EmailAddressInputProps = {
	defaultValue?: string;
	onSubmit: (model: EmailAddressModel) => void;
};
export const EmailAddressInput = forwardRef<HTMLInputElement, EmailAddressInputProps>(
	({ onSubmit, defaultValue }, ref) => {
		const [model, setModel] = useState({ email: defaultValue || '' });
		const [suggestLogin, setSuggestLogin] = useState(false);
		const [isEmailValid, setIsEmailValid] = useState(validateEmailAddress(defaultValue || ''));
		const { api } = useConfig();
		const t = useTranslation().form;
		const [touched, setTouched] = useState(false);
		const [focused, setFocused] = useState(false);
		const [dirty, setDirty] = useState(true);

		async function checkEmailAddress(emailAddress: string) {
			emailAddress = encodeURIComponent(emailAddress);
			return (await axios.get<ApiEmailInfo>(`${api}${Res.emailInfo.uri({ emailAddress })}`)).data;
		}

		function validateEmailAddress(emailAddress: string): boolean {
			return /\S+@\S+\.\S+/.test(emailAddress);
		}

		async function handleEmailSubmit(e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) {
			e.preventDefault();
			if (!isEmailValid) return;

			const res = await checkEmailAddress(model.email);
			if (res.result.suggestLogin) {
				setSuggestLogin(true);
			}
			onSubmit(model);
			setDirty(false);
		}

		function handleEmailInputChange(email: string) {
			if (suggestLogin) setSuggestLogin(false);
			setDirty(true);
			setIsEmailValid(validateEmailAddress(email));
			setModel({ email });
		}

		return (
			<form onSubmit={handleEmailSubmit} className="checkout-email-form">
				<div className="email-inputs">
					<TextInput
						value={model.email}
						type="email"
						ref={ref}
						placeholder={`${t.email} ${
							!focused && touched && !isEmailValid && model.email !== ''
								? ` - ${t.emailSeemsInvalid}`
								: ''
						}`}
						onFocus={() => {
							setFocused(true);
						}}
						onBlur={() => {
							if (!touched) setTouched(true);
							setFocused(false);
						}}
						onChange={(e) => {
							handleEmailInputChange(e.target.value);
						}}
						className="email-address-input"
					/>
					{!suggestLogin && dirty && (
						<Button className="email-address-button" onClick={handleEmailSubmit}>
							{t.continue}
						</Button>
					)}
				</div>

				{suggestLogin && <LoginForm email={model.email} />}
			</form>
		);
	}
);
